import type { TypographyV3Element } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

// Represents legal section/article/sub-section references in the document's hierarchy
type LevelType = 'I' | 'A' | 'i';

export const HEADING: {
  [x in LevelType]: {
    Element: TypographyV3Element;
    classNames: string;
  };
} = {
  I: {
    Element: 'h2',
    classNames:
      'bold list-upper-roman ml-20 text-[1.375rem] tracking-normal lg:tracking-tight lg:text-[2rem]',
  },
  A: {
    Element: 'h3',
    classNames:
      'bold text-[1.25rem] list-upper-alpha ml-24 mb-4 tracking-normal leading-relaxed font-normal lg:text-[1.75rem]',
  },
  i: {
    Element: 'h4',
    classNames: 'text-[1.25rem] font-normal list-lower-roman ml-24',
  },
};

interface LegalHeadingProps {
  readonly level: LevelType;
  readonly tx: MessageKey;
  readonly className?: string;
  readonly children?: ReactNode;
  readonly id?: string;
}

const LegalHeading = ({
  level,
  tx: text,
  className,
  children,
  id = '',
}: LegalHeadingProps): JSX.Element => {
  const { Element, classNames } = HEADING[level];
  const t = useTranslations();
  return (
    <>
      <Element
        className={cx('list-item uppercase', classNames, className)}
        id={id}
      >
        {t.rich(text)}
      </Element>

      {/* Reset Typograpy for nested children */}
      {children && <section className="normal-case">{children}</section>}
    </>
  );
};

export default LegalHeading;
